import { Grid } from '@mui/material';
import type { BillingCycleInvoiceType } from '@weft/api-definition';
import { BILLING_CYCLE_INVOICE_TYPES } from '@weft/api-definition';
import { fromNullToUndefined, withDefault } from '@weft/utils';
import { useState } from 'react';
import { Helmet } from 'react-helmet';
import { NavLink } from 'react-router-dom';
import { VyPageLayout } from '../../components/new-design/layout/VyPageLayout';
import { withGlobalSearchParams } from '../../components/utility/GlobalSearchParamsBoundary';
import { withSuspenseBoundary } from '../../components/utility/SuspenseBoundary';
import { useBillingCycleInvoices } from '../../hooks/requests/use-billing-cycle-invoices/use-billing-cycle-invoices';
import { useBillingCyclePage } from '../../hooks/requests/use-billing-cycle-page';
import { useGetApprovalFlowInvoicesList } from '../../hooks/requests/use-get-approval-flow-invoices-list';
import { usePageParam } from '../../hooks/search-params/use-page-param';
import { usePageSizeParam } from '../../hooks/search-params/use-page-size-param';
import { useDebounce } from '../../hooks/use-debounce';
import { useQueryInputParams } from '../invoices/InvoicesPage/invoices-page-hooks/use-query-input-param';
import { BillingCycleSidebar } from './billing-cycle-components/BillingCycleSidebar';
import { BillingCycleTable } from './billing-cycle-components/BillingCycleTable';

const BillingCyclePage: React.FC = () => {
  const [selectedInvoiceType, setSelectedInvoiceType] = useState<BillingCycleInvoiceType>(BILLING_CYCLE_INVOICE_TYPES.REGULAR);

  const [nullablePage, setPage] = usePageParam();
  const [pageSize] = usePageSizeParam();
  const [query] = useQueryInputParams();
  const page = withDefault(nullablePage, 0);
  const search = fromNullToUndefined(query);
  const debouncedSearch = useDebounce(search);

  const { data: billingCyclePage, isFetching: isFetchingBillingCyclePage } = useBillingCyclePage({ search: debouncedSearch });

  const regularBillingCycleInvoicesData = useBillingCycleInvoices({ page, pageSize, search: debouncedSearch, billingCycleInvoiceType: BILLING_CYCLE_INVOICE_TYPES.REGULAR }, {
    enabled: selectedInvoiceType === BILLING_CYCLE_INVOICE_TYPES.REGULAR,
  });
  const inspectionBillingCycleInvoicesData = useBillingCycleInvoices({ page, pageSize, search: debouncedSearch, billingCycleInvoiceType: BILLING_CYCLE_INVOICE_TYPES.INSPECTION }, {
    enabled: selectedInvoiceType === BILLING_CYCLE_INVOICE_TYPES.INSPECTION,
  });
  const oldBillingCycleInvoicesData = useBillingCycleInvoices({ page, pageSize, search: debouncedSearch, billingCycleInvoiceType: BILLING_CYCLE_INVOICE_TYPES.OLD }, {
    enabled: selectedInvoiceType === BILLING_CYCLE_INVOICE_TYPES.OLD,
  });

  const regularBillingCycleInvoices = withDefault(regularBillingCycleInvoicesData.data?.invoices, []);
  const inspectionBillingCycleInvoices = withDefault(inspectionBillingCycleInvoicesData.data?.invoices, []);
  const oldBillingCycleInvoices = withDefault(oldBillingCycleInvoicesData.data?.invoices, []);

  useGetApprovalFlowInvoicesList(['previousCycle']);

  return (
    <>
      <Helmet>
        <title>Monthly Closure</title>
      </Helmet>
      <VyPageLayout
        path={[
          <NavLink to="/invoices">Invoices</NavLink>,
          'Monthly Closure',
        ]}
        title="Monthly Closure"
      >
        <Grid
          container
          className="flex h-full"
          spacing={2}
        >
          <Grid
            item
            xs={12}
            lg={3}
          >
            <BillingCycleSidebar
              selectedInvoiceType={selectedInvoiceType}
              onInvoicePreviewClick={(selected) => {
                setSelectedInvoiceType(selected);
                setPage(0);
              }}
              regularBillingCycleInvoicesCount={withDefault(billingCyclePage?.regularBillingCycleInvoicesCount, 0)}
              inspectionBillingCycleInvoicesCount={withDefault(billingCyclePage?.inspectionBillingCycleInvoicesCount, 0)}
              oldBillingCycleInvoicesCount={withDefault(billingCyclePage?.oldBillingCycleInvoicesCount, 0)}
              overcharges={withDefault(billingCyclePage?.overcharges, 0)}
              loading={isFetchingBillingCyclePage}
            />
          </Grid>

          <Grid
            item
            xs={12}
            lg={9}
            className="h-full"
            overflow="hidden"
          >
            {selectedInvoiceType === BILLING_CYCLE_INVOICE_TYPES.REGULAR && (
              <BillingCycleTable
                items={regularBillingCycleInvoices}
                itemsCount={withDefault(billingCyclePage?.regularBillingCycleInvoicesCount, 0)}
                loadingItems={regularBillingCycleInvoicesData.isFetching}
              />
            )}
  
            {selectedInvoiceType === BILLING_CYCLE_INVOICE_TYPES.INSPECTION && (
              <BillingCycleTable
                items={inspectionBillingCycleInvoices}
                itemsCount={withDefault(billingCyclePage?.inspectionBillingCycleInvoicesCount, 0)}
                loadingItems={inspectionBillingCycleInvoicesData.isFetching}
              />
            )}

            {selectedInvoiceType === BILLING_CYCLE_INVOICE_TYPES.OLD && (
              <BillingCycleTable
                items={oldBillingCycleInvoices}
                itemsCount={withDefault(billingCyclePage?.oldBillingCycleInvoicesCount, 0)}
                loadingItems={oldBillingCycleInvoicesData.isFetching}
              />
            )}
          </Grid>
        </Grid>
      </VyPageLayout>
    </>
  );
};

const BillingCyclePageWithSuspenseBoundary = withSuspenseBoundary(withGlobalSearchParams(BillingCyclePage));
export { BillingCyclePageWithSuspenseBoundary as BillingCyclePage };